import type { Table, Table2, Field, Column2, TableBorder, HeaderRowDefault, HeaderCellDefault, FormatTypes, Fields, HTMLSpacing } from '../../models';
import { getQuillFromField, getQuillFromText, getQuillFromVariables } from '../base-objects';
import { getExtendedFormatObject } from '../style';
import { cloneObject } from '../../utilities';

const borderTable: TableBorder = {
  top: { color: '#ccc', size: 1, type: 'solid' },
  left: { color: '#ccc', size: 0, type: 'solid' },
  bottom: { color: '#ccc', size: 0, type: 'solid' },
  right: { color: '#ccc', size: 1, type: 'solid' },
};

const borderHeader: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 1, type: 'solid' },
  bottom: { color: '#ccc', size: 1, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

const borderColumn: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 1, type: 'solid' },
  bottom: { color: '#ccc', size: 0, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

const borderRow: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 0, type: 'solid' },
  bottom: { color: '#ccc', size: 1, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

export const containerPadding: HTMLSpacing = {
  top: 0,
  topUnit: 'px',
  right: 0,
  rightUnit: 'px',
  bottom: 0,
  bottomUnit: 'px',
  left: 0,
  leftUnit: 'px',
};

const defaultStyle = getExtendedFormatObject();

const styleBackground = { backgroundColor: '#fff' };

const defaultFormat = {
  formatType: 'string' as FormatTypes,
  locale: 'de-DE', // Get it dynamically
  number: {
    numberFormat: '.0f',
    decimalPlaces: 0,
    digitGrouping: true,
    shorten: false,
    showSign: false,
    showZero: true,
  },
  date: {
    inputFormat: 'YYYY',
    dateFormat: 'YYYY',
  },
  prefix: '',
  suffix: '',
};

const idxCol: Column2 = {
  enabled: true,
  label: '#',
  quillValue: '',
  quillBackground: { backgroundColor: '#fff' },
  fontFormat: defaultStyle,
  fieldName: 'idx-23degrees',
  fieldDataType: 'string',
  fieldType: 'miscField',
  sequence: -1,
  size: {
    default: {
      flex: 1,
      maxWidth: 45,
    },
  },
  responsive: {
    collapse: false,
    desktop: false,
    mobile: false,
  },
  border: cloneObject(borderColumn),
  ellipsis: false,
  sortOrder: null,
};

// export const table1ToTable2 = (typeSpecific: TypeSpecific, fields: Field[]): Table2 => {
export const table1ToTable2 = (table: Table, fields: Fields): Table2 => {
  const columns = table.columns.filter((col) => !col.autoIdx);
  const headerItems: HeaderCellDefault[] = [];
  const columnItems: Column2[] = [];
  // Add Columns // filter(col => !col.autoIdx)
  for (const [idx, col] of columns.entries()) {
    const field: Field = fields[col.definition.field];
    // Exclude some fields
    if (['isoField'].includes(field.fieldType)) continue;
    // Columns
    const column: Column2 = {
      enabled: col.enabled,
      label: col.definition.headerName,
      quillValue: field ? getQuillFromField(field) : '',
      quillBackground: { backgroundColor: '#fff' },
      emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),

      fieldName: col.definition.field,
      fieldDataType: field ? field.dataType : 'string',
      fieldType: field ? field.fieldType : 'miscField',
      size: {
        default: {
          flex: 1,
        },
      },
      responsive: {
        collapse: false,
        desktop: true,
        mobile: true,
      },
      ellipsis: false,
      sequence: field.sequence ?? idx,
      sortOrder: null,
    };

    columnItems.push(column);

    // Headers
    const headerCell: HeaderCellDefault = {
      id: `default-${idx}`,
      label: col.definition.headerName,
      fieldName: col.definition.field,
      sequence: field.sequence ?? idx,
      quillValue: getQuillFromText(col.definition.headerName),
      // fontFormat: defaultStyle,
      colSpan: 1,
      rowSpan: 1,
      // border: borderHeaderCell,
    };

    headerItems.push(headerCell);
  }

  const defaultHeaderRow: HeaderRowDefault = {
    id: 'default',
    position: 0,
    enabled: true,
    items: headerItems,
    // fontFormat: defaultStyle,
    // border: borderHeaderRow,
  };

  const table2: Table2 = {
    columns: {
      ellipsis: true,
      fontFormat: cloneObject(defaultStyle),
      emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),
      items: columnItems,
      idx: idxCol,
      background: '#fff',
      border: cloneObject(borderColumn),
      mixedFormat: cloneObject(defaultFormat),
      sortable: true,
    },
    rows: {
      items: [],
      border: borderRow,
      stripe: {
        enabled: false,
        color: '#f1f1f1',
      },
    },
    header: {
      enabled: true,
      rows: [defaultHeaderRow],
      fixed: false,
      fontFormat: cloneObject(defaultStyle),
      background: '#fff',
      border: cloneObject(borderHeader),
      // columnBorder: true
    },
    style: {
      dynamicHeight: !!table?.settings.dynamicHeight,
      cellPadding: 6,
      unitPadding: {
        top: 6,
        topUnit: 'px',
        right: 6,
        rightUnit: 'px',
        bottom: 6,
        bottomUnit: 'px',
        left: 6,
        leftUnit: 'px',
      },
      containerPadding: cloneObject(containerPadding),
      border: cloneObject(borderTable),
      mobileBreakpoint: 600,
      background: '#fff',
      borderDefaultColor: '#ccc',
      defaultFontFormat: cloneObject(defaultStyle),
    },
    footer: {
      enabled: false,
      style: {
        border: cloneObject(borderTable),
        font: cloneObject(defaultStyle),
        background: cloneObject(styleBackground),
      },
    },
    pagination: {
      enabled: false,
      itemsPerPage: 10,
      fontFormat: cloneObject(defaultStyle),
      infoQuillValue: getQuillFromVariables('{{first_item}} - {{last_item}}'),
      navQuillValue: getQuillFromVariables('{{curr_page}} / {{total_pages}}'),
      quillBackground: { backgroundColor: '#fff' },
      background: '#f8f8f8',
      iconsSize: 18,
      icons: {
        first: { value: 'icon:playback-first' },
        prev: { value: 'icon:playback-prev' },
        next: { value: 'icon:playback-next' },
        last: { value: 'icon:playback-last' },
      },
      foreground: '#111', // Element colors DEPRECATED
    },
    sorting: {
      enabled: false,
      order: [],
      columns: [],
    },

    search: {
      enabled: false,
      clearable: true,
      showTotal: false,
      placeholder: 'Search',
    },

    heatmap: {
      enabled: false,
      legend: {
        enabled: false,
        title: 'Legend',
        position: 'top-right',
        barHeight: 14,
        width: 50,
        widthUnit: '%',
        fontFormat: cloneObject(defaultStyle),
      },
    },
    charts: {
      items: [],
    },
  };

  return table2;
};

export const columnIdx: Column2 = {
  enabled: false,
  label: '#index',
  quillValue: getQuillFromVariables('{{index}}'),
  quillBackground: { backgroundColor: '#fff' },

  fieldName: 'index',
  fieldDataType: 'string',
  fieldType: 'miscField',
  size: {
    default: {
      flex: 1,
    },
  },
  responsive: {
    collapse: false,
    desktop: false,
    mobile: false,
  },
  ellipsis: false,
  sequence: -1,
  sortOrder: null,
  touched: true,
};

// Index Column Header
export const headerCellIdx: HeaderCellDefault = {
  id: `default-#`,
  label: '#',
  fieldName: 'index',
  sequence: -1,
  quillValue: getQuillFromText('#'),
  // fontFormat: defaultStyle,
  colSpan: 1,
  rowSpan: 1,
  // border: borderHeaderCell,
};
